import { Box, Typography, Card, Grid, useTheme } from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";
import AssignmentLateIcon from "@mui/icons-material/AssignmentLate";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import ErrorIcon from "@mui/icons-material/Error";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

function SummaryCards({ data }) {
  const theme = useTheme();

  if (!data) return null;

  const {
    total_contacts = 0,
    playlists_requiring_review_count = 0,
    contacts = [],
  } = data;

  const statusCounts = contacts.reduce(
    (acc, contact) => {
      const status = contact.competency_status_category;
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    },
    {
      not_yet_reviewed: 0,
      overdue: 0,
      due_soon: 0,
      up_to_date: 0,
    }
  );

  const generalCards = [
    {
      title: "Total Contacts",
      value: total_contacts,
      icon: <GroupIcon sx={{ fontSize: 24, color: "white" }} />,
      trend: total_contacts > 0 ? `+${total_contacts}` : "0",
      gradient: "linear-gradient(135deg, #6B8DE3 0%, #3B5998 100%)",
      subtitle: "Compared to last month",
    },
    {
      title: "Playlists Requiring Review",
      value: playlists_requiring_review_count,
      icon: <PlaylistAddCheckIcon sx={{ fontSize: 24, color: "white" }} />,
      trend:
        playlists_requiring_review_count > 0
          ? `+${playlists_requiring_review_count}`
          : "0",
      gradient: "linear-gradient(135deg, #9370DB 0%, #4B0082 100%)",
      subtitle: "Pending reviews",
    },
  ];

  const competencyCards = [
    {
      title: "Not Yet Reviewed",
      value: statusCounts.not_yet_reviewed,
      icon: <AssignmentLateIcon sx={{ fontSize: 24, color: "white" }} />,
      trend:
        statusCounts.not_yet_reviewed > 0
          ? `+${statusCounts.not_yet_reviewed}`
          : "0",
      gradient: "linear-gradient(135deg, #FF6B6B 0%, #C62E2E 100%)",
      subtitle: "No review record",
    },
    {
      title: "Overdue",
      value: statusCounts.overdue,
      icon: <ErrorIcon sx={{ fontSize: 24, color: "white" }} />,
      trend: statusCounts.overdue > 0 ? `+${statusCounts.overdue}` : "0",
      gradient: "linear-gradient(135deg, #FFA07A 0%, #FF4500 100%)",
      subtitle: "Require immediate action",
    },
    {
      title: "Due Soon",
      value: statusCounts.due_soon,
      icon: <ScheduleIcon sx={{ fontSize: 24, color: "white" }} />,
      trend: statusCounts.due_soon > 0 ? `+${statusCounts.due_soon}` : "0",
      gradient: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
      subtitle: "Review approaching",
    },
    {
      title: "Up to Date",
      value: statusCounts.up_to_date,
      icon: <CheckCircleIcon sx={{ fontSize: 24, color: "white" }} />,
      trend: statusCounts.up_to_date > 0 ? `+${statusCounts.up_to_date}` : "0",
      gradient: "linear-gradient(135deg, #50C878 0%, #2E8B57 100%)",
      subtitle: "No immediate action needed",
    },
  ];

  const renderCard = (card) => (
    <Card
      sx={{
        background: card.gradient,
        color: "white",
        borderRadius: "10px",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.3s ease",
        [theme.breakpoints.up("sm")]: {
          maxWidth: 990,
        },
        "&:hover": {
          transform: "translateY(-8px)",
          boxShadow: "0 12px 20px rgba(0,0,0,0.2)",
          "& .card-shine": {
            transform: "translateX(100%)",
          },
        },
      }}
    >
      <Box
        className="card-shine"
        sx={{
          position: "absolute",
          top: 0,
          left: "-100%",
          width: "100%",
          height: "100%",
          background:
            "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
          transition: "transform 0.5s ease",
        }}
      />
      <Box sx={{ p: 1.5 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Box
              sx={{
                backgroundColor: "rgba(255,255,255,0.2)",
                borderRadius: "6px",
                p: 0.5,
                display: "inline-flex",
                mb: 0.5,
              }}
            >
              {card.icon}
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              {card.value}
              {parseFloat(card.trend) > 0 ? (
                <TrendingUpIcon sx={{ fontSize: 16, opacity: 0.8 }} />
              ) : (
                <TrendingDownIcon sx={{ fontSize: 16, opacity: 0.8 }} />
              )}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" justifyContent="space-between" mt={0.5}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              opacity: 0.9,
            }}
          >
            {card.title}
          </Typography>
        </Box>
        <Typography
          variant="caption"
          sx={{
            opacity: 0.7,
            mt: 0.25,
          }}
        >
          {card.subtitle}
        </Typography>
      </Box>
    </Card>
  );

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {generalCards.map((card) => (
          <Grid item xs={12} sm={6} md={6} key={card.title}>
            {renderCard(card)}
          </Grid>
        ))}
      </Grid>

      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1,color: theme.palette.text.primary }}>
        Competency Status Overview
      </Typography>

      <Grid container spacing={2}>
        {competencyCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.title}>
            {renderCard(card)}
          </Grid>
        ))}
      </Grid>
    </>
  );
}

export default SummaryCards;
